import { validateField, validateForm } from '../../utils/form-validator-utils';
import { resendOTP, verifyEmailOTP, getAccounts } from '../../services/auth-service';
import React, { useEffect, useState } from 'react';
import Container from '../../components/shared/container';
import CustomInput from '../../components/shared/custom-input';
import Button from '../../components/shared/custom-btn';
import CreateAccountModal from './create-accounts';

const VerifyModal = ({ setShowModal, onSuccess }) => {
    const [signupKeys, setSignupKeys] = useState('');
    const [formData, setFormData] = useState({
        code: ''
    });
    const [errors, setErrors] = useState({
        code: '',
        apiError: ''
    });
    const [successMessage, setSuccessMessage] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [showCreateModal, setShowCreateModal] = useState(false);
    const [user, setUser] = useState();
    const [countdown, setCountdown] = useState(10);
    const [canResend, setCanResend] = useState(false);

    // Countdown timer for resend OTP
    useEffect(() => {
        let timer;
        if (countdown > 0) {
            timer = setTimeout(() => setCountdown(countdown - 1), 1000);
        } else {
            setCanResend(true);
        }
        return () => clearTimeout(timer);
    }, [countdown]);

    // get user from local
    useEffect(() => {
        const data = localStorage.getItem("user");
        if (data) {
            try {
                const parsedUser = JSON.parse(data);
                setUser(parsedUser);
            } catch (error) {
                console.error("Error parsing user from localStorage:", error);
                setUser(null);
            }
        } else {
            setUser(null);
        }
    }, []);


    // Check verification keys
    useEffect(() => {
        const timeout = setTimeout(() => {
            const keys = localStorage.getItem('signupKeys');
            setSignupKeys(keys);
        }, 50);
        return () => clearTimeout(timeout);
    }, []);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData((prev) => ({
            ...prev,
            [name]: value
        }));

        if (errors[name]) {
            setErrors((prev) => ({
                ...prev,
                [name]: validateField(name, value)
            }));
        }
    };

    const handleResendOTP = async () => {
        setErrors((prev) => ({ ...prev, apiError: '' }));
        setSuccessMessage('');

        try {
            setIsSubmitting(true);
            const response = await resendOTP(user.email, 'signup');

            if (response.success) {
                if (response.data?.data?.verificationKey) {
                    localStorage.setItem('signupKeys', response.data.data.verificationKey);
                    setSignupKeys(response.data.data.verificationKey);
                }
                setSuccessMessage(response.data.message || 'OTP resent successfully!');
                // Reset countdown
                setCanResend(false);
                setCountdown(10);
            } else {
                setErrors((prev) => ({
                    ...prev,
                    apiError: response.data.message || 'Failed to resend OTP. Please try again.'
                }));
            }
        } catch (error) {
            setErrors((prev) => ({
                ...prev,
                apiError: error.message || 'An unexpected error occurred.'
            }));
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleAccountComplete = () => {
        setShowCreateModal(false);
        setShowModal(false);
        if (onSuccess) onSuccess();
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        const { errors: validationErrors, isValid } = validateForm(formData, ['code']);
        setErrors({ ...validationErrors, apiError: '' });
        setSuccessMessage('');
        if (!isValid) return;

        setIsSubmitting(true);
        try {
            const response = await verifyEmailOTP({
                code: formData.code,
                verificationKey: signupKeys
            });

            if (response.success) {
                localStorage.removeItem('signupKeys');
                const token = localStorage.getItem('authToken');

                const storedUser = JSON.parse(localStorage.getItem('user') || '{}');
                const updatedUser = {
                    ...storedUser,
                    status: 'active'
                };
                localStorage.setItem('user', JSON.stringify(updatedUser));

                setSuccessMessage(response.data.message || 'Email verified successfully!');

                // Fetch business accounts
                const accountResponse = await getAccounts(token);
                if (accountResponse.success) {
                    const accounts = accountResponse.data.data || [];
                    if (accounts.length === 0) {
                        setShowCreateModal(true);
                    } else {
                        // Automatically select the first account and store in localStorage
                        const selectedAccount = accounts[0];
                        localStorage.setItem('accountId', JSON.stringify(selectedAccount.accountId));
                        setTimeout(() => {
                            setShowModal(false);
                            if (onSuccess) onSuccess();
                        }, 2000);
                    }
                } else {
                    setErrors((prev) => ({
                        ...prev,
                        apiError: accountResponse.data.message || 'Failed to fetch business accounts.'
                    }));
                    setTimeout(() => {
                        setShowModal(false);
                        if (onSuccess) onSuccess();
                    }, 2000);
                }
            } else {
                setErrors((prev) => ({
                    ...prev,
                    apiError: response.data.message || 'OTP verification failed. Please try again.'
                }));
            }
        } catch (error) {
            setErrors((prev) => ({
                ...prev,
                apiError: error.message || 'An unexpected error occurred.'
            }));
            setTimeout(() => {
                setShowModal(false);
                if (onSuccess) onSuccess();
            }, 2000);
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <>
            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                <Container className="!px-4 gap-6 max-w-md w-full rounded-lg">
                    <div className="flex bg-white flex-col items-center justify-center py-6 lg:px-6 px-4 rounded-lg">
                        <div className="w-full">
                            <h2 className="text-2xl text-center font-bold text-gray-800 mb-3">Verify Email</h2>
                            <p className='text-gray-600 text-sm mb-3'>Your OTP has been emailed to <span className='font-semibold'>{user?.email}</span>. Check spam folder as well.</p>

                            {successMessage && (
                                <div className="mb-4 p-3 bg-green-100 text-green-700 text-sm rounded-lg text-center">
                                    {successMessage}
                                </div>
                            )}
                            {errors.apiError && (
                                <div className="mb-4 p-3 bg-red-100 text-red-700 text-sm rounded-lg text-center">
                                    {errors.apiError}
                                </div>
                            )}

                            <form onSubmit={handleSubmit} className="space-y-4">
                                <CustomInput
                                    label="Enter OTP Code"
                                    name="code"
                                    type="text"
                                    value={formData.code}
                                    onChange={handleChange}
                                    inputStyle={`p-2 w-full bg-white border !text-sm rounded-lg ${errors.code ? 'border-red-500' : 'border-border'}`}
                                    placeholder="Enter 6-digit OTP"
                                    error={errors.code}
                                    maxLength={6}
                                    onlyNumbers={true}
                                />
                                <Button
                                    type="submit"
                                    label={isSubmitting ? 'Verifying...' : 'Verify OTP'}
                                    loading={isSubmitting}
                                    disabled={isSubmitting}
                                    className={`w-full bg-blue-800 text-white font-medium py-2 px-4 rounded-lg ${isSubmitting ? 'opacity-50 cursor-not-allowed' : 'hover:bg-blue-900'}`}
                                />

                                <div className="text-center">
                                    {canResend ? (
                                        <button
                                            onClick={handleResendOTP}
                                            className="text-blue-600 text-sm hover:underline"
                                            type="button"
                                            disabled={isSubmitting}
                                        >
                                            Resend OTP
                                        </button>
                                    ) : (
                                        <p className="text-gray-500 text-sm">
                                            Request new OTP in {countdown} seconds
                                        </p>
                                    )}
                                </div>
                            </form>
                        </div>
                    </div>
                </Container>
            </div>

            {showCreateModal && (
                <CreateAccountModal
                    setShowCreateModal={setShowCreateModal}
                    token={localStorage.getItem('authToken')}
                    onSuccess={handleAccountComplete}
                />
            )}
        </>
    );
};

export default VerifyModal;